import { Tooltip } from '@chakra-ui/react';
import type { PropsWithChildren } from 'react';

import { FormatType } from '../../types';
import { getFormatter, withExplicitSign } from '../../utilities';
import tooltipStyles from '../../utilities/chart-tooltip.module.css';
import styles from './chord-chart.module.css';
import type { CardinalPlacement } from './chord-tooltip-position';
import type { ChordFlowMode } from './types';

type ChordGroupTooltipProps = PropsWithChildren & {
  label: string;
  inflow: number;
  outflow: number;
  isOpen: boolean;
  placement: CardinalPlacement;
  flowMode: ChordFlowMode;
};

export const ChordGroupTooltip = ({
  label,
  inflow,
  outflow,
  children,
  isOpen,
  placement,
  flowMode,
}: ChordGroupTooltipProps) => {
  return (
    <Tooltip
      label={
        <TooltipContent
          label={label}
          inflow={inflow}
          outflow={outflow}
          flowMode={flowMode}
        />
      }
      placement={placement}
      padding="8px 12px"
      borderRadius="4px"
      hasArrow
      isOpen={isOpen}
      maxW="none"
      data-testid="chord-chart__group-tooltip"
      className={tooltipStyles.surface}
    >
      {children}
    </Tooltip>
  );
};

const format = getFormatter(FormatType.INTEGER);
const formatSigned = withExplicitSign(format);

const TooltipContent = ({
  label,
  inflow,
  outflow,
  flowMode,
}: {
  label: string;
  inflow: number;
  outflow: number;
  flowMode: ChordFlowMode;
}) => {
  const isNet = flowMode === 'net';
  const inflowLabel = isNet ? 'Net Inflow' : 'Inflow';
  const outflowLabel = isNet ? 'Net Outflow' : 'Outflow';
  // Net flow collapses each pair to a single direction, so every node ends up
  // with either inflow or outflow at zero. Hide the zero side to avoid noise.
  const showInflow = !isNet || inflow > 0;
  const showOutflow = !isNet || outflow > 0;
  // In directional mode both sides are shown, so surface the net (inflow −
  // outflow) here instead of as a separate chart mode.
  const netFlow = inflow - outflow;
  return (
    <div className={styles.tooltipBlock}>
      <div className={styles.tooltipTitle}>{label}</div>
      <dl className={styles.tooltipMetrics}>
        {showInflow && (
          <>
            <dt>{inflowLabel}</dt>
            <dd>{format(inflow)}</dd>
          </>
        )}
        {showOutflow && (
          <>
            <dt>{outflowLabel}</dt>
            <dd>{format(outflow)}</dd>
          </>
        )}
        {!isNet && (
          <>
            <dt>Net flow</dt>
            <dd>{formatSigned(netFlow)}</dd>
          </>
        )}
      </dl>
    </div>
  );
};
